// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import type { DiscoveryResult } from '../projects/discover';

export interface DiscoveryPanelOptions {
  discovery: DiscoveryResult;
  // Called when the user clicks "Régénérer avec mes réponses". Receives
  // the answers keyed by question id. Caller awaits the regeneration
  // (typically magnifyIdea + auto-save + card replace) and should resolve
  // on success / reject on failure ; the panel reflects the state via
  // data-state on the submit button.
  onSubmit: (answers: Record<string, string>) => Promise<void>;
  // 2026-05-22 — draft autosave. Persists answers WITHOUT re-magnifying, so
  // typed text survives a reload/navigation. The prior design only saved
  // answers as a side-effect of onSubmit (which ALSO re-magnifies → keyless
  // users 403'd, and anyone who navigated away before submitting lost their
  // input). Fired on every textarea blur and on the explicit "Enregistrer"
  // button. Opt-in: when absent, neither the blur autosave nor the button
  // render (keeps callers that don't persist unchanged).
  onSaveDraft?: (answers: Record<string, string>) => Promise<void>;
  onCancel?: () => void;
  // 2026-05-18 v26 — pre-fill answer textareas from prior persisted state.
  // Used when re-opening a saved project that already has stored discovery
  // answers. Keyed by question id (matches DiscoveryQuestion.id from the
  // edge fn). Missing keys → empty textarea, as before.
  initialAnswers?: Record<string, string>;
  // 2026-05-18 v26 — when set, a small link "🔄 Refaire la découverte"
  // appears at the bottom of the panel. Calling it triggers a fresh
  // Anthropic discovery call (re-scanning current attachments) which
  // replaces the panel with the new questions. Used to give the user
  // an escape hatch when their files changed since the last discovery.
  onRerun?: () => Promise<void>;
}

// Collect the current answer textareas into a {questionId: value} map.
// Shared by the re-magnify submit, the blur autosave, and the explicit save.
function collectAnswers(root: HTMLElement): Record<string, string> {
  const answers: Record<string, string> = {};
  root.querySelectorAll<HTMLTextAreaElement>('[data-component="discovery-a"]').forEach((ta) => {
    const qid = ta.getAttribute('data-qid');
    if (qid) answers[qid] = ta.value;
  });
  return answers;
}

// Renders the conversational-refinement panel : 1-sentence discovery
// summary at the top + 3-5 textareas (one per question) + submit/cancel.
// Visual : a soft amber accent so it reads as "Genesis is talking to you"
// distinct from the purple "Régénérer enrichi" / teal "Push to Forgejo".
export function renderDiscoveryPanel(opts: DiscoveryPanelOptions): HTMLElement {
  const root = document.createElement('section');
  root.className = 'discovery-panel';
  root.setAttribute('data-component', 'discovery-panel');

  const questionsHtml = opts.discovery.questions.map((q) => {
    const prior = opts.initialAnswers?.[q.id] ?? '';
    return `
    <div class="discovery-panel__q" data-component="discovery-q" data-qid="${escapeHtml(q.id)}">
      <label class="discovery-panel__q-label" for="discovery-a-${escapeHtml(q.id)}">${escapeHtml(q.question)}</label>
      <textarea
        id="discovery-a-${escapeHtml(q.id)}"
        data-component="discovery-a"
        data-qid="${escapeHtml(q.id)}"
        class="discovery-panel__a"
        rows="2"
        placeholder="${escapeHtml(q.placeholder ?? t('discovery.answerPlaceholder'))}"
      >${escapeHtml(prior)}</textarea>
    </div>
    `;
  }).join('');

  const skippedHtml = opts.discovery.attachments_skipped.length > 0 ? `
    <details class="discovery-panel__skipped">
      <summary>${escapeHtml(t('discovery.skippedSummary')).replace('{n}', String(opts.discovery.attachments_skipped.length))}</summary>
      <ul>${opts.discovery.attachments_skipped.map((s) => `<li><code>${escapeHtml(s.name)}</code> — ${escapeHtml(s.reason)}</li>`).join('')}</ul>
    </details>
  ` : '';

  root.innerHTML = `
    <div class="discovery-panel__header">
      <span class="discovery-panel__chip">${escapeHtml(t('discovery.chip'))}</span>
      <h3 class="discovery-panel__title">${escapeHtml(t('discovery.title'))}</h3>
    </div>
    <p class="discovery-panel__summary">${escapeHtml(opts.discovery.discovery_summary)}</p>
    <div class="discovery-panel__questions">${questionsHtml}</div>
    ${skippedHtml}
    <div class="discovery-panel__actions">
      <button type="button" data-component="discovery-submit" class="discovery-panel__submit" data-state="idle">
        <span data-component="discovery-submit-label">${escapeHtml(t('discovery.submit'))}</span>
      </button>
      ${opts.onSaveDraft ? `<button type="button" data-component="discovery-save" class="discovery-panel__save" data-state="idle">${escapeHtml(t('discovery.saveDraft'))}</button>` : ''}
      ${opts.onCancel ? `<button type="button" data-component="discovery-cancel" class="discovery-panel__cancel">${escapeHtml(t('discovery.cancel'))}</button>` : ''}
    </div>
    ${opts.onRerun ? `
      <div class="discovery-panel__rerun">
        <button type="button" data-component="discovery-rerun" class="discovery-panel__rerun-btn" data-state="idle">${escapeHtml(t('discovery.rerun'))}</button>
        <small class="discovery-panel__rerun-hint">${escapeHtml(t('discovery.rerunHint'))}</small>
      </div>
    ` : ''}
  `;

  const submitBtn = root.querySelector<HTMLButtonElement>('[data-component="discovery-submit"]')!;
  const submitLabel = root.querySelector<HTMLElement>('[data-component="discovery-submit-label"]')!;
  submitBtn.addEventListener('click', async () => {
    if (submitBtn.getAttribute('data-state') !== 'idle') return;
    const answers = collectAnswers(root);
    submitBtn.setAttribute('data-state', 'busy');
    submitBtn.disabled = true;
    submitLabel.textContent = t('discovery.submitBusy');
    try {
      await opts.onSubmit(answers);
      // On success parent replaces the panel — no further state needed.
    } catch (err) {
      console.warn('[discovery] submit failed', err);
      submitBtn.setAttribute('data-state', 'error');
      submitLabel.textContent = t('discovery.submitError');
      submitBtn.disabled = false;
      setTimeout(() => {
        submitBtn.setAttribute('data-state', 'idle');
        submitLabel.textContent = t('discovery.submit');
      }, 2400);
    }
  });

  if (opts.onSaveDraft) {
    // Autosave on blur: leaving any field persists the whole answer set, so
    // a reload/navigation never loses typed text. Silent (no UI churn) —
    // the explicit button below is the visible "I saved" affordance.
    root.querySelectorAll<HTMLTextAreaElement>('[data-component="discovery-a"]').forEach((ta) => {
      ta.addEventListener('blur', () => {
        // Silent autosave — swallow failures (the explicit "Enregistrer" button
        // surfaces errors). Never let a blur produce an unhandled rejection.
        opts.onSaveDraft!(collectAnswers(root)).catch((err) => {
          console.warn('[discovery] blur autosave failed (non-fatal)', err);
        });
      });
    });
    const saveBtn = root.querySelector<HTMLButtonElement>('[data-component="discovery-save"]')!;
    saveBtn.addEventListener('click', async () => {
      if (saveBtn.getAttribute('data-state') === 'busy') return;
      saveBtn.setAttribute('data-state', 'busy');
      saveBtn.textContent = t('discovery.saveDraftBusy');
      try {
        await opts.onSaveDraft!(collectAnswers(root));
        saveBtn.setAttribute('data-state', 'done');
        saveBtn.textContent = t('discovery.saveDraftDone');
      } catch (err) {
        console.warn('[discovery] save draft failed', err);
        saveBtn.setAttribute('data-state', 'error');
        saveBtn.textContent = t('discovery.saveDraftError');
      }
      setTimeout(() => {
        saveBtn.setAttribute('data-state', 'idle');
        saveBtn.textContent = t('discovery.saveDraft');
      }, 1800);
    });
  }

  if (opts.onCancel) {
    root.querySelector<HTMLButtonElement>('[data-component="discovery-cancel"]')!
      .addEventListener('click', () => opts.onCancel!());
  }

  if (opts.onRerun) {
    const rerunBtn = root.querySelector<HTMLButtonElement>('[data-component="discovery-rerun"]')!;
    rerunBtn.addEventListener('click', async () => {
      if (rerunBtn.getAttribute('data-state') !== 'idle') return;
      rerunBtn.setAttribute('data-state', 'busy');
      rerunBtn.textContent = t('discovery.rerunBusy');
      try {
        await opts.onRerun!();
        // Parent typically replaces the panel with the fresh discovery ;
        // no need to reset state here.
      } catch (err) {
        console.warn('[discovery] rerun failed', err);
        rerunBtn.setAttribute('data-state', 'error');
        rerunBtn.textContent = t('discovery.rerunError');
        setTimeout(() => {
          rerunBtn.setAttribute('data-state', 'idle');
          rerunBtn.textContent = t('discovery.rerun');
        }, 2400);
      }
    });
  }

  return root;
}
